import React, { forwardRef, useMemo, useEffect, useRef } from 'react';

// DEPENDENCIES
import identity from 'lodash/identity';
import isFunction from 'lodash/isFunction';
import sum from 'lodash/sum';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

// LOCAL VARIABLES
import { DEFAULT_ACTION_ITEM_WIDTH, PLACEHOLDER } from '../../variables';

// HELPERS
import { useListData } from 'helpers/getListData';
import usePrevious from 'helpers/usePrevious';

// LOCAL COMPONENTS
import ActionsCell from '../../tools/ActionsCell';
import LinkCell from '../../tools/LinkCell';
import TableSticky from '../../tools/TableSticky';
import TableHeader from './TableHeader';
import TableSortedBody from './TableSortedBody';

// STYLES
import styles from './sortedGroup.module.scss';

// MAIN COMPONENT
const SortedGroup = forwardRef(
  (
    {
      index, //
      placeholder = PLACEHOLDER,
      result,
      rowHeight,
      setRowHeight,
      setTotalColumnsWidth,
      style: styleProp = {},
      titleFormat = identity,
      toolbar,
      width,
      tableBefore,
      ...props
    },
    ref
  ) => {
    const { title, data } = result[index] || {};

    // REFS
    const groupRef = useRef({});

    // HOOKS / PROPS
    const {
      actions, //
      columns: rawColumns,
      preferences: { filters } = {}
    } = Object.assign(useListData() || {}, props);

    // MEMOS
    const columns = useMemo(() => {
      let transformed = rawColumns.map(({ component, label, key, to, width, ...c }) => {
        const column = {
          ...c,
          header: label,
          id: key,
          accessorKey: key,
          size: width,
          to,
          enableResizing: false
        };
        if (component) column.cell = component;
        else if (to) column.cell = LinkCell;
        return column;
      });
  
      if (actions?.length) {
        const actionsColumn = {
          header: '',
          id: 'actions',
          headerClassName: 'flex-grow-0',
          className: 'flex-grow-0',
          cell: p => <ActionsCell {...p} column={{ actions }} />,
          size: sum(actions.map(a => a.width || DEFAULT_ACTION_ITEM_WIDTH)),
          enableResizing: false
        };
        transformed = [...transformed, actionsColumn];
      }

      return transformed;
    }, [actions, rawColumns]);

    const groupStyle = useMemo(() => {
      const style = { ...styleProp, height: 'auto' };
      return style;
    }, [styleProp]);

    // REACT TABLE
    const table = useReactTable({
      columns,
      data: data || [],
      enableSorting: false,
      getCoreRowModel: getCoreRowModel(),
      state: {
        sorting: [{ id: filters?.sort, desc: filters?.order === 'D' }]
      },
    });

    const { rows } = table.getRowModel();

    const totalColumnsWidth = table.getTotalSize();

    const prevTotalColumnsWidth = usePrevious(totalColumnsWidth);

    // EFFECTS
    useEffect(() => {
      if (!prevTotalColumnsWidth !== totalColumnsWidth) {
        setTotalColumnsWidth(totalColumnsWidth);
      }
    }, [prevTotalColumnsWidth, totalColumnsWidth, setTotalColumnsWidth]);

    useEffect(() => {
      if (groupRef?.current && setRowHeight) {
        setRowHeight(index, groupRef.current.clientHeight, ref);
      }
    }, [index, ref, setRowHeight]);

    // RENDER
    return (
      <div className={styles.container} ref={groupRef} style={groupStyle}>
        {index === 0 && tableBefore}
        <TableSticky className={styles.sticky}>
          <div className={styles.header}>
            <h3 className={styles.title}>{titleFormat(title)}</h3>
            <div className={styles.toolbar}>{isFunction(toolbar) ? toolbar(data) : toolbar}</div>
          </div>
        </TableSticky>
        <table className="table table-striped flex-grow-1 mb-3" style={{ width }}>
          <div className={styles.top}>
            <TableHeader
              className={styles.header}
              groups={table.getHeaderGroups()}
            />
          </div>
          <TableSortedBody
            rows={rows}
            rowHeight={rowHeight}
            placeholder={placeholder}
          />
        </table>
      </div>
    );
  }
);

// EXPORT
export default SortedGroup;
